import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { Document, Page } from 'react-pdf'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { Loader2, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DriveLoadingPlaceholder } from '@/components/common/DriveLoadingPlaceholder'
import { PdfErrorBoundary } from '../PdfErrorBoundary'
import { PdfToolbar, type PreviewKind } from '../PdfToolbar'
import { highlightTextItem } from './pdfSearch'
import { readSheetStarts, sheetTabs, type SheetTab } from './sheetTabs'
import { SheetTabBar } from './SheetTabBar'
import { captureAnchor, restoreScrollTop, type ZoomAnchor } from './zoomAnchor'
import {
  CONTAINER_PADDING,
  PT_TO_PX,
  fitZoom,
  wheelZoom,
  zoomInStep,
  zoomOutStep,
  type FitMode,
  type Size,
} from './zoom'
import type { BinaryRendererProps } from '../types'
import { logError } from '@/lib/logger'
import '@/lib/pdf-worker'
// Only needed while searching — the text layer stays off otherwise (see
// `renderTextLayer` below), and this rule set targets nothing until it is on.
import 'react-pdf/dist/Page/TextLayer.css'

/** What react-pdf reports for a loaded page: its size at the current scale and its own. */
interface PageSize {
  width: number
  height: number
  originalWidth: number
  originalHeight: number
}

// A page's canvas stays under this many device pixels: Safari paints a larger
// one blank. A spreadsheet drawn as one page per sheet can be far taller than
// any document page, and then renders at a lower pixel ratio instead.
const MAX_CANVAS_PIXELS = 16_000_000

/** Pixel ratio for a page of `width` × `height` CSS pixels, capped so its canvas fits. */
export function canvasPixelRatio(width: number, height: number, screenRatio: number): number {
  const area = width * height
  if (area <= 0) return screenRatio
  return Math.min(screenRatio, Math.sqrt(MAX_CANVAS_PIXELS / area))
}

// Pixels scrolled per Arrow Up/Down press — small enough to read fluently while
// held down (key repeat).
const KEY_SCROLL_STEP = 60

interface PdfRendererProps extends BinaryRendererProps {
  fileName: string
  pageNumber?: number
  /**
   * Initial fit mode. Unset, it follows the file (cdb_webos#1477): a workbook
   * opens at 100 % as in Excel, slides show a whole slide, anything else fits
   * the width.
   */
  defaultFitMode?: 'width' | 'page'
  onPageChange?: (page: number) => void
  onNumPagesChange?: (numPages: number) => void
  /** Highlight every occurrence of this term. Empty or unset turns search off. */
  searchTerm?: string
  /**
   * Reserve a full-container-height slot per page instead of just the page's
   * own scaled height (cdb_webos#737). Without this, a page shorter than the
   * container leaves its own row exactly as short, so the next page's top
   * edge peeks into the same viewport — fine for browsing a real multi-page
   * document, but wrong for a preview meant to show exactly one page/slide
   * at a time (e.g. the office-template per-layout sampler, where the first
   * sampled layout is often sparse and the following one bled into view).
   */
  pageFraming?: boolean
  /**
   * The pages are the sheets of a workbook, one page each (cdb_tqs#2162).
   * When the PDF names its sheets (LibreOffice writes a bookmark per sheet),
   * one sheet is shown at a time with Excel's sheet tabs below (cdb_webos#1465).
   */
  sheetLayout?: boolean
}

/** What a page is to the reader; a converted `.pptx` still holds slides. */
function previewKind(fileName: string, sheetLayout: boolean): PreviewKind {
  if (sheetLayout) return 'sheet'
  return /\.pptx$/i.test(fileName) ? 'slides' : 'document'
}

const DEFAULT_FIT: Record<PreviewKind, FitMode> = { document: 'width', slides: 'page', sheet: 'custom' }

/** Where the reader was looking sideways when a zoom started, in content pixels at `scale`. */
interface SideAnchor {
  x: number
  offset: number
  scale: number
}

export function PdfRenderer({ fileUrl, fileName, pageNumber: initialPage, defaultFitMode, onPageChange, onNumPagesChange, searchTerm, pageFraming = false, sheetLayout = false }: PdfRendererProps) {
  const { t } = useTranslation()
  const kind = previewKind(fileName, sheetLayout)

  const [numPages, setNumPages] = useState(0)
  // The page currently in view — derived from scroll position, drives the toolbar counter.
  const [pageNumber, setPageNumber] = useState(initialPage || 1)
  const [fitMode, setFitMode] = useState<FitMode>(defaultFitMode ?? DEFAULT_FIT[kind])
  // The zoom chosen by hand (menu, − / +, wheel); a fit mode computes its own.
  const [customZoom, setCustomZoom] = useState(1)
  // The scroll container's size, followed so that a fit mode keeps fitting
  // when the panel or window changes size.
  const [box, setBox] = useState<Size>({ width: 0, height: 0 })
  // The page a fit mode measures, in PDF points: the first page on screen.
  const [fitPage, setFitPage] = useState<Size | null>(null)
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({ x: 0, y: 0 })
  // Each page's own size once it has loaded: sheets differ in size, and a page
  // reserves its real height and gets a canvas it can paint.
  const [pageSizes, setPageSizes] = useState<Record<number, { width: number; height: number }>>({})
  // The workbook's sheets once the outline is read; null shows every page.
  const [sheets, setSheets] = useState<SheetTab[] | null>(null)
  const [activeSheet, setActiveSheet] = useState(0)
  // Pages wait for the outline, so a workbook does not flash all its sheets.
  const [sheetsReady, setSheetsReady] = useState(!sheetLayout)
  const loadIdRef = useRef(0)

  // The PDF pages on screen: the active sheet's, or all of them. `pageNumber`
  // and everything scroll-related count within this list.
  const shownPages = sheets
    ? sheets[activeSheet].pages
    : Array.from({ length: numPages }, (_, index) => index + 1)
  const shownCount = shownPages.length

  const fitZooms = {
    width: fitPage ? fitZoom('width', box, fitPage) : null,
    page: fitPage ? fitZoom('page', box, fitPage) : null,
  }
  // A fit mode is not stored as a number: it is worked out on every render, so
  // it follows the panel's size and the sheet on screen until the reader zooms.
  const zoom = fitMode === 'custom' ? customZoom : fitZooms[fitMode] ?? customZoom
  const scale = zoom * PT_TO_PX

  const containerRef = useRef<HTMLDivElement>(null)
  const pageRefs = useRef<Array<HTMLDivElement | null>>([])
  const hasScrolledToInitialRef = useRef(false)
  // Mirrors for handlers bound once (keys, wheel, resize), which must read the
  // current values without re-binding on every render.
  const pageNumberRef = useRef(pageNumber)
  const numPagesRef = useRef(numPages)
  const zoomRef = useRef(zoom)
  const fitModeRef = useRef(fitMode)
  const fitPageRef = useRef(fitPage)
  const boxRef = useRef(box)
  const hoveredRef = useRef(false)
  pageNumberRef.current = pageNumber
  numPagesRef.current = shownCount
  zoomRef.current = zoom
  fitModeRef.current = fitMode
  fitPageRef.current = fitPage

  // Estimated rendered page height at the current scale — reserves layout space
  // for not-yet-rendered pages so scrolling stays stable. 0 until the first page
  // reports its size.
  const estimatedPageHeight = fitPage ? fitPage.height * scale : 0
  const frameHeight = Math.max(box.height - 2 * CONTAINER_PADDING, 0)

  // With `pageFraming`, every row is at least as tall as the container, so a
  // shorter page sits centred in its own full-viewport slot instead of
  // leaving room for the next page to show through underneath it.
  const pageRowMinHeight = pageFraming ? Math.max(estimatedPageHeight, frameHeight) : estimatedPageHeight

  const term = searchTerm?.trim() ?? ''

  const onPageLoadSuccess = useCallback((index: number, page: PageSize) => {
    setPageSizes((sizes) =>
      sizes[index] ? sizes : { ...sizes, [index]: { width: page.originalWidth, height: page.originalHeight } }
    )
  }, [])

  const scrollToPage = useCallback((page: number) => {
    const container = containerRef.current
    const el = pageRefs.current[page - 1]
    if (!container || !el) return
    container.scrollTo({ top: Math.max(el.offsetTop - 8, 0), behavior: 'smooth' })
  }, [])

  const goToPage = useCallback(
    (page: number) => scrollToPage(Math.min(Math.max(page, 1), numPagesRef.current)),
    [scrollToPage]
  )

  // Notify parent of the visible page (deep-linking).
  const shownPage = shownPages[pageNumber - 1] ?? pageNumber
  useEffect(() => {
    onPageChange?.(shownPage)
  }, [shownPage, onPageChange])

  // react-pdf hands the text-layer item back as an HTML string, so both the
  // text and the term are escaped before a <mark> is spliced in.
  const customTextRenderer = useCallback(
    ({ str }: { str: string }) => highlightTextItem(str, term),
    [term]
  )

  // Jump to the deep-linked page once, after the first page is measured so the
  // reserved heights make the offset accurate. Only on load — reacting to every
  // initialPage change would fight the scroll-driven page tracking.
  const initialPosition = initialPage ? shownPages.indexOf(initialPage) + 1 : 0
  useEffect(() => {
    if (!isDocumentLoaded || !sheetsReady || hasScrolledToInitialRef.current) return
    if (initialPosition <= 1) {
      hasScrolledToInitialRef.current = true
      return
    }
    if (estimatedPageHeight <= 0) return
    hasScrolledToInitialRef.current = true
    requestAnimationFrame(() => scrollToPage(initialPosition))
  }, [isDocumentLoaded, sheetsReady, initialPosition, estimatedPageHeight, scrollToPage])

  // Track which page is most visible in the scroll container → toolbar counter.
  useEffect(() => {
    if (!isDocumentLoaded || numPages === 0) return
    const container = containerRef.current
    if (!container) return

    const ratios = new Map<number, number>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const idx = Number((entry.target as HTMLElement).dataset.pageIndex)
          ratios.set(idx, entry.intersectionRatio)
        }
        let best = -1
        let bestRatio = -1
        ratios.forEach((ratio, idx) => {
          if (ratio > bestRatio || (ratio === bestRatio && (best === -1 || idx < best))) {
            bestRatio = ratio
            best = idx
          }
        })
        if (best >= 0 && bestRatio > 0) setPageNumber(best + 1)
      },
      { root: container, threshold: [0, 0.25, 0.5, 0.75, 1] },
    )

    pageRefs.current.forEach((el) => { if (el) observer.observe(el) })
    return () => observer.disconnect()
  }, [isDocumentLoaded, numPages, sheetsReady, activeSheet])

  function onDocumentLoadSuccess(pdf: PDFDocumentProxy) {
    const pages = pdf.numPages
    // Reset per-document state so a newly opened file measures its own pages
    // and re-runs the deep-link scroll.
    pageRefs.current = []
    hasScrolledToInitialRef.current = false
    setPageSizes({})
    setFitPage(null)
    setNumPages(pages)
    setPageNumber(initialPage || 1)
    setIsDocumentLoaded(true)
    setError(null)
    onNumPagesChange?.(pages)
    setSheets(null)
    setActiveSheet(0)
    if (sheetLayout) loadSheets(pdf, pages)
  }

  function loadSheets(pdf: PDFDocumentProxy, pages: number) {
    const loadId = ++loadIdRef.current
    setSheetsReady(false)
    readSheetStarts(pdf)
      .then((starts) => sheetTabs(starts, pages))
      .catch((err) => {
        // Without names the sheets are still all there, one below the other.
        logError(err, { context: 'PdfRenderer: reading the sheet names', fileName })
        return null
      })
      .then((tabs) => {
        if (loadId !== loadIdRef.current) return
        setSheets(tabs)
        const opened = tabs && initialPage ? tabs.findIndex((tab) => tab.pages.includes(initialPage)) : -1
        setActiveSheet(Math.max(opened, 0))
        if (tabs) setPageNumber(opened >= 0 && initialPage ? tabs[opened].pages.indexOf(initialPage) + 1 : 1)
        setSheetsReady(true)
      })
  }

  function onDocumentLoadError(err: any) {
    console.error('PDF load error:', err)
    setIsDocumentLoaded(false)

    if (err?.message?.includes('Unexpected server response')) {
      setError(t('pdfViewer.pdfNoLongerAvailable'))
    } else {
      setError(t('pdfViewer.errorLoadingPdf'))
    }
  }

  // The first page on screen is what the fit modes measure.
  function onFirstPageLoadSuccess(page: PageSize) {
    setFitPage((current) =>
      current && current.width === page.originalWidth && current.height === page.originalHeight
        ? current
        : { width: page.originalWidth, height: page.originalHeight }
    )
  }

  // Where the reader was looking when the zoom started, so they can be put back
  // there afterwards (#660). See `zoomAnchor.ts` for why it is a page and a
  // fraction rather than a proportion of the whole document.
  const zoomAnchorRef = useRef<ZoomAnchor | null>(null)
  const sideAnchorRef = useRef<SideAnchor | null>(null)
  // The scale the laid-out pages have, which a sideways anchor is measured in.
  const renderedScaleRef = useRef(scale)

  const pageBoxes = () =>
    pageRefs.current.map((el) => ({ top: el?.offsetTop ?? 0, height: el?.offsetHeight ?? 0 }))

  const captureZoomAnchor = useCallback((point?: { clientX: number; clientY: number }) => {
    const container = containerRef.current
    if (!container) return
    // Default to the middle of the viewport — that is what the reader is
    // looking at when they press a toolbar button. A wheel zoom passes the
    // pointer instead, which is where they are looking then.
    const rect = container.getBoundingClientRect()
    const offsetY = point ? point.clientY - rect.top : container.clientHeight / 2
    const offsetX = point ? point.clientX - rect.left : container.clientWidth / 2
    zoomAnchorRef.current = captureAnchor(pageBoxes(), container.scrollTop, offsetY)
    sideAnchorRef.current = { x: container.scrollLeft + offsetX, offset: offsetX, scale: renderedScaleRef.current }
  }, [])

  // Runs before paint, after the pages have taken their new size: the reserved
  // heights and widths follow `scale` in the same commit, so the anchor's page
  // is already where it will end up.
  useLayoutEffect(() => {
    const container = containerRef.current
    const anchor = zoomAnchorRef.current
    const side = sideAnchorRef.current
    zoomAnchorRef.current = null
    sideAnchorRef.current = null
    renderedScaleRef.current = scale
    if (!container) return
    if (anchor) {
      const scrollTop = restoreScrollTop(pageBoxes(), anchor)
      if (scrollTop !== null) container.scrollTop = scrollTop
    }
    if (side && side.scale > 0) {
      const ratio = scale / side.scale
      container.scrollLeft = Math.max(0, (side.x - CONTAINER_PADDING) * ratio + CONTAINER_PADDING - side.offset)
    }
  }, [scale])

  const zoomTo = useCallback(
    (next: number, point?: { clientX: number; clientY: number }) => {
      captureZoomAnchor(point)
      zoomRef.current = next
      setCustomZoom(next)
      setFitMode('custom')
    },
    [captureZoomAnchor]
  )

  const fit = useCallback(
    (mode: 'width' | 'page') => {
      setFitMode(mode)
      if (mode === 'width') {
        captureZoomAnchor()
        return
      }
      // A whole page is the current one from its top edge, not two halves.
      const page = pageNumberRef.current
      const target = fitPageRef.current ? fitZoom('page', boxRef.current, fitPageRef.current) : null
      if (target === null || target === zoomRef.current) {
        scrollToPage(page)
        return
      }
      zoomAnchorRef.current = { page: page - 1, fraction: 0, offset: 8 }
      sideAnchorRef.current = null
    },
    [captureZoomAnchor, scrollToPage]
  )

  const zoomInTarget = zoomInStep(zoom)
  const zoomOutTarget = zoomOutStep(zoom)

  // Another sheet starts at its top-left corner, as in Excel. A chosen zoom
  // stays; a fit mode fits the new sheet.
  const selectSheet = useCallback(
    (index: number) => {
      if (!sheets || index === activeSheet) return
      const first = pageSizes[sheets[index].pages[0] - 1]
      if (first) setFitPage(first)
      pageRefs.current = []
      zoomAnchorRef.current = null
      sideAnchorRef.current = null
      setActiveSheet(index)
      setPageNumber(1)
      containerRef.current?.scrollTo({ top: 0, left: 0 })
    },
    [sheets, activeSheet, pageSizes]
  )

  const hasContainer = !!fileUrl && !error

  // Follow the container's size. A resize that changes what a fit mode comes
  // to keeps the reader at the same place, like any other zoom.
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    let frame = 0
    const measure = () => {
      const next = { width: container.clientWidth, height: container.clientHeight }
      const previous = boxRef.current
      if (next.width === previous.width && next.height === previous.height) return
      const mode = fitModeRef.current
      const page = fitPageRef.current
      if (mode !== 'custom' && page && fitZoom(mode, previous, page) !== fitZoom(mode, next, page)) {
        captureZoomAnchor()
      }
      boxRef.current = next
      setBox(next)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    })
    observer.observe(container)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [hasContainer, captureZoomAnchor])

  // Ctrl/⌘ + wheel and a trackpad pinch zoom around the pointer. React binds
  // wheel listeners passively, so only a native one can keep the browser from
  // zooming the whole page instead.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    function onWheel(e: WheelEvent) {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      const delta = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaY
      zoomTo(wheelZoom(zoomRef.current, delta), e)
    }
    container.addEventListener('wheel', onWheel, { passive: false })
    return () => container.removeEventListener('wheel', onWheel)
  }, [hasContainer, zoomTo])

  // Keys, scoped to the active window only. Up/Down scroll, Left/Right jump
  // pages, Ctrl/⌘ + 0 goes back to 100 %. Bound to the document, but guarded so
  // it fires only when THIS renderer's window is the active one — never when
  // minimized, behind another window, or the inactive half of a split view
  // (those windows carry data-active="false"; minimized ones are additionally
  // `inert`).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const container = containerRef.current
      if (!container) return

      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        // Only while the reader is on this preview; anywhere else it stays the
        // browser's own zoom reset.
        if (!hoveredRef.current && !container.contains(document.activeElement)) return
        e.preventDefault()
        zoomTo(1)
        return
      }

      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return

      // Never hijack arrow keys while the user is typing in a form control.
      const active = document.activeElement
      if (active instanceof HTMLElement && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT' || active.isContentEditable)) return

      // Only act when the surrounding window is active. A null wrapper (renderer
      // used outside a window) falls through to "act".
      const windowEl = container.closest('[data-active]')
      if (windowEl && windowEl.getAttribute('data-active') !== 'true') return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          container.scrollBy({ top: KEY_SCROLL_STEP })
          break
        case 'ArrowUp':
          e.preventDefault()
          container.scrollBy({ top: -KEY_SCROLL_STEP })
          break
        case 'ArrowRight':
          e.preventDefault()
          scrollToPage(Math.min(pageNumberRef.current + 1, numPagesRef.current))
          break
        case 'ArrowLeft':
          e.preventDefault()
          scrollToPage(Math.max(pageNumberRef.current - 1, 1))
          break
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [scrollToPage, zoomTo])

  function handleMouseDown(e: React.MouseEvent) {
    if (e.button === 0) {
      setIsPanning(true)
      setPanStart({ x: e.clientX, y: e.clientY })
      e.preventDefault()
    }
  }

  function handleMouseMove(e: React.MouseEvent) {
    if (!isPanning || !containerRef.current) return
    containerRef.current.scrollLeft -= e.clientX - panStart.x
    containerRef.current.scrollTop -= e.clientY - panStart.y
    setPanStart({ x: e.clientX, y: e.clientY })
  }

  function handleMouseUp() { setIsPanning(false) }
  function handleMouseLeave() {
    hoveredRef.current = false
    setIsPanning(false)
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-destructive font-medium">{error}</p>
        </div>
      </div>
    )
  }

  if (!fileUrl) {
    return (
      <DriveLoadingPlaceholder
        message={t('pdfViewer.noPdfSelected')}
        helperText={t('pdfViewer.openFromDrive')}
      />
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-2 min-h-0">
      <div
        ref={containerRef}
        data-testid="pdf-scroll"
        // The stable gutter keeps the width a fit mode measures from changing
        // when the scrollbar comes and goes, which would refit in a loop.
        className={`relative flex-1 min-h-0 border border-[var(--window-border-color)] rounded-lg overflow-auto [scrollbar-gutter:stable] bg-[var(--window-background)] p-4 ${
          isPanning ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseEnter={() => { hoveredRef.current = true }}
        onMouseLeave={handleMouseLeave}
      >
        <Document
          key={fileName}
          file={fileUrl}
          // Sheets start at their left edge, as in Excel; centring each one
          // inside the widest put the narrower ones off to the side.
          className={`flex flex-col gap-4 w-max min-w-full ${sheetLayout ? 'items-start' : 'items-center'}`}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--text-secondary)]" />
            </div>
          }
          error={
            <div className="flex items-center justify-center p-8">
              <div className="text-destructive">{t('pdfViewer.errorLoadingPdf')}</div>
            </div>
          }
        >
          {isDocumentLoaded && sheetsReady && (
            <PdfErrorBoundary
              fallback={
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[var(--text-secondary)]" />
                </div>
              }
            >
              {shownPages.map((pageNo, index) => {
                const size = pageSizes[pageNo - 1]
                const rowHeight = size
                  ? pageFraming
                    ? Math.max(size.height * scale, frameHeight)
                    : size.height * scale
                  : pageRowMinHeight
                // The width is reserved too, so a zoom can scroll sideways to
                // its anchor before the canvas has been redrawn.
                const rowWidth = (size ?? fitPage)?.width ?? 0
                return (
                <div
                  key={pageNo}
                  data-page-index={index}
                  ref={(el) => { pageRefs.current[index] = el }}
                  className={sheetLayout ? 'flex items-start justify-start' : 'flex items-center justify-center'}
                  style={{
                    minHeight: rowHeight > 0 ? `${rowHeight}px` : undefined,
                    minWidth: rowWidth > 0 ? `${rowWidth * scale}px` : undefined,
                  }}
                >
                  <Page
                    pageNumber={pageNo}
                    scale={scale}
                    devicePixelRatio={
                      size
                        ? canvasPixelRatio(size.width * scale, size.height * scale, window.devicePixelRatio || 1)
                        : window.devicePixelRatio || 1
                    }
                    onLoadSuccess={(page: PageSize) => {
                      onPageLoadSuccess(pageNo - 1, page)
                      if (index === 0) onFirstPageLoadSuccess(page)
                    }}
                    loading={
                      <div className="flex items-center justify-center p-8">
                        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-secondary)]" />
                      </div>
                    }
                    // Off by default: the text layer costs real time on long
                    // documents and nothing reads it. Search needs it, so it
                    // comes on for exactly as long as a term is active.
                    renderTextLayer={!!term}
                    customTextRenderer={term ? customTextRenderer : undefined}
                    renderAnnotationLayer={false}
                  />
                </div>
                )
              })}
            </PdfErrorBoundary>
          )}
        </Document>
      </div>
      <PdfToolbar
        kind={kind}
        pageNumber={pageNumber}
        numPages={shownCount}
        showPageNavigation={!sheets || shownCount > 1}
        onGoToPage={goToPage}
        zoom={zoom}
        fitMode={fitMode}
        fitZooms={fitZooms}
        canZoomIn={zoomInTarget !== null}
        canZoomOut={zoomOutTarget !== null}
        onZoomIn={() => { if (zoomInTarget !== null) zoomTo(zoomInTarget) }}
        onZoomOut={() => { if (zoomOutTarget !== null) zoomTo(zoomOutTarget) }}
        onFit={fit}
        onZoomTo={zoomTo}
      >
        {sheets && (
          <SheetTabBar names={sheets.map((sheet) => sheet.name)} active={activeSheet} onSelect={selectSheet} />
        )}
      </PdfToolbar>
    </div>
  )
}
